import ApiBase from './Api'
import { getStoredLanguage, resolveLanguage } from '../i18n/languagePreference'
import { getInvestorAmountInCash, getInvestorAmountInPercentage } from './utils'
import {
    ConvertibleActionItem,
    ConvertibleSingleItem,
    DeleteConvertibleRequest,
    GetAllConvertiblesRequest,
    GetConvertiblesByCompanyRequest,
    GetConvertibleSingleItemRequest,
    PatchConvertibleRequest,
    PostConvertibleRequest,
} from '@finance-lab/utils/types'

const setupDerivedFields = (convertible: ConvertibleSingleItem) => {
    const loanAmount = convertible.convertibleActions.reduce((acc: number, action: ConvertibleActionItem) => {
        if (action.actionType === 'new_loan' && action.loanAmount !== null) {
            acc += action.loanAmount
        }
        return acc
    }, 0)

    return {
        ...convertible,
        investors: convertible.investors.map((investor) => ({
            ...investor,
            amountInCash: getInvestorAmountInCash(investor, loanAmount),
            amountInPercentage: getInvestorAmountInPercentage(investor, loanAmount),
        })),
    }
}

const CONVERTIBLES_API_PATHS = {
    CONVERTIBLES: '/convertibles',
    CONVERTIBLE: (id: string) => `/convertibles/${id}`,
}

export type ConvertibleInput = {
    shareId: string
}

class ConvertiblesApi extends ApiBase {
    async findAll() {
        const response = await this._getRequest<GetAllConvertiblesRequest>(CONVERTIBLES_API_PATHS.CONVERTIBLES)

        return response
    }

    async findByConvertibleId(convertibleId: string, enrichConvertible = true) {
        if (!convertibleId) {
            return null
        }
        const preferredLanguage = resolveLanguage(getStoredLanguage())
        const fetchedConvertible = await this._getRequest<GetConvertibleSingleItemRequest>(
            CONVERTIBLES_API_PATHS.CONVERTIBLE(convertibleId),
            {
                queryParameters: { preferredLanguage },
            },
        )

        if (enrichConvertible) {
            return setupDerivedFields(fetchedConvertible)
        }

        return fetchedConvertible
    }

    async findByCompanyId(companyId: string) {
        if (!companyId) {
            return []
        }
        const fetchedConvertibles = await this._getRequest<GetConvertiblesByCompanyRequest>(
            CONVERTIBLES_API_PATHS.CONVERTIBLES,
            {
                queryParameters: { companyId },
            },
        )

        return fetchedConvertibles
    }

    async create(convertible: PostConvertibleRequest['Body']) {
        const response = await this._postRequest<PostConvertibleRequest>(
            CONVERTIBLES_API_PATHS.CONVERTIBLES,
            convertible,
        )

        return response
    }

    async update(id: string, convertibleToUpdate: PatchConvertibleRequest['Body']) {
        const response = await this._patchRequest<PatchConvertibleRequest>(
            CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + id,
            convertibleToUpdate,
        )

        return response
    }

    async delete(id: string) {
        const response = await this._deleteRequest<DeleteConvertibleRequest>(
            CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + id,
        )

        return response
    }
}

export default ConvertiblesApi
